// next-app/src/hooks/use-survey.ts
'use client';

import { api } from '@/lib/api-client';
import { useSurveyStore } from '@/store/survey-store';
import type { EventSurveyConfig, SurveyConfigResponse } from '@/types';
import { useMutation, useQuery } from '@tanstack/react-query';
import { queryKeys } from './query-keys';

// ============================================================================
// Survey Config Query
// ============================================================================

/**
 * Hook to fetch survey configuration by hash.
 *
 * Usage:
 * ```tsx
 * const { data, isLoading } = useSurveyConfig(hash);
 * ```
 */
export function useSurveyConfig(hash: string) {
  const setConfig = useSurveyStore((state) => state.setConfig);

  return useQuery({
    queryKey: queryKeys.survey.config(hash),
    queryFn: async () => {
      const { response, result } = await api.getSurveyConfig(hash);

      if (!response.ok) {
        throw new Error('Failed to load survey configuration');
      }

      if (result?.config) {
        setConfig(result.config);
      }

      return result as SurveyConfigResponse;
    },
    enabled: Boolean(hash),
    staleTime: 5 * 60 * 1000, // 5 minutes - survey config rarely changes
  });
}

// ============================================================================
// Pre-Registration Event Query
// ============================================================================

/**
 * Hook to fetch event info by pre-registration hash.
 * Returns event_id and event_name.
 */
export function usePreRegEvent(hash: string) {
  return useQuery({
    queryKey: queryKeys.survey.preRegEvent(hash),
    queryFn: async () => {
      const { response, result } = await api.getEventSurveyConfig(hash);

      if (!response.ok) {
        throw new Error('Failed to load event information');
      }

      return result as EventSurveyConfig;
    },
    enabled: Boolean(hash),
    staleTime: 5 * 60 * 1000,
  });
}

// ============================================================================
// Submit Survey Mutation
// ============================================================================

interface UseSubmitSurveyOptions {
  onSuccess?: (participantId?: number) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook to submit survey response.
 *
 * Usage:
 * ```tsx
 * const submitSurvey = useSubmitSurvey(hash, { onSuccess: () => router.push('/success') });
 * submitSurvey.mutate(formData);
 * ```
 */
export function useSubmitSurvey(accessKey: string, options: UseSubmitSurveyOptions = {}) {
  const { setSubmitting, setError } = useSurveyStore();

  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      setSubmitting(true);
      setError(null);

      const { response, result } = await api.submitSurvey(data, accessKey);

      if (!response.ok) {
        throw new Error(result?.message || 'Failed to submit survey');
      }

      return result;
    },
    onSuccess: (data) => {
      setSubmitting(false);
      options.onSuccess?.(data?.participant_id);
    },
    onError: (error: Error) => {
      setSubmitting(false);
      setError(error.message);
      console.error('[useSubmitSurvey] Error:', error.message);
      options.onError?.(error);
    },
  });
}

// ============================================================================
// Submit Vehicles of Interest Mutation
// ============================================================================

/**
 * Hook to submit vehicles of interest after survey.
 */
export function useSubmitVehiclesOfInterest(accessKey: string) {
  return useMutation({
    mutationFn: async (data: { participant_id: number; vehicle_ids: number[] }) => {
      const vehiclesOfInterest = data.vehicle_ids.map((vehicle_id) => ({
        participant_id: data.participant_id,
        vehicle_id,
      }));

      const { response, result } = await api.submitVehiclesOfInterest(
        vehiclesOfInterest,
        accessKey
      );

      if (!response.ok) {
        throw new Error('Failed to submit vehicles of interest');
      }

      return result;
    },
    onError: (error: Error) => {
      console.error('[useSubmitVehiclesOfInterest] Error:', error.message);
    },
  });
}

// ============================================================================
// Submit Vehicles Driven Mutation
// ============================================================================

/**
 * Hook to submit vehicles driven after survey.
 */
export function useSubmitVehiclesDriven(accessKey: string) {
  return useMutation({
    mutationFn: async (data: { participant_id: number; vehicle_ids: number[] }) => {
      const { response, result } = await api.submitVehiclesDriven(data, accessKey);

      if (!response.ok) {
        throw new Error('Failed to submit vehicles driven');
      }

      return result;
    },
    onError: (error: Error) => {
      console.error('[useSubmitVehiclesDriven] Error:', error.message);
    },
  });
}
